import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import SEO from '../../components/SEO';
import { useLanguage } from '../../context/LanguageContext';

const AIRBNB_URL = 'https://www.airbnb.fr/rooms/1645057277425002541';

const IMAGES = [
  '/photo-site/quartz/072CCA5C-15A2-45AF-8354-CC363343543F.JPG',
  '/photo-site/quartz/0BB32FC4-9833-4A9B-8202-85A648FCF0FD.JPG',
  '/photo-site/quartz/139FF3F7-FA2A-46E3-A7D3-FF2008007032.JPG',
  '/photo-site/quartz/50885530-774A-4363-B733-170E90B93ACB.JPG',
  '/photo-site/quartz/5A23341E-9215-4B4B-B491-942BF71DF9BA.JPG',
  '/photo-site/quartz/909CF4A0-F3EC-4A43-90D1-3B2B023639A2.JPG',
  '/photo-site/quartz/953CF745-30B6-403E-BD52-4DC5AB67065A.JPG',
  '/photo-site/quartz/9DF68265-7F76-4716-99BC-8D48504BABC4.JPG',
  '/photo-site/quartz/IMG_7074.jpeg',
  '/photo-site/quartz/IMG_7237.jpeg',
  '/photo-site/quartz/IMG_7238.jpeg',
];

const AppartementQuartz = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [mainImage, setMainImage] = useState(IMAGES[0]);
  const equipements = t('quartz.equip');

  return (
    <>
    <SEO
      title={t('seo.quartz.title')}
      description={t('seo.quartz.description')}
      keywords="appartement quartz la grave, duplex 2 personnes la grave, location meije, hébergement la grave hautes-alpes"
      url="/appartements/appartement-2-personnes-quartz"
      image="https://marmottejoyeuse.fr/photo-site/quartz/072CCA5C-15A2-45AF-8354-CC363343543F.JPG"
    />
    <div className="container mx-auto px-4 pt-28 pb-16 min-h-screen">
      <button
        onClick={() => navigate('/appartements')}
        className="fixed top-24 left-4 md:left-8 z-10 btn btn-circle btn-ghost bg-base-200/50 backdrop-blur-sm hover:bg-base-200"
        aria-label={t('quartz.back')}
      >
        ←
      </button>

      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <span className="text-primary font-semibold text-sm">{t('quartz.label')}</span>
          <h1 className="text-4xl font-bold mt-1">{t('quartz.name')}</h1>
          <p className="text-base-content/60 mt-1">{t('quartz.tagline')}</p>
        </div>

        <div className="mb-10 bg-base-200 rounded-xl p-4">
          <img
            src={mainImage}
            alt="Appartement Quartz - La Marmotte Joyeuse"
            className="w-full h-[400px] object-cover rounded-lg mb-4 transition-all duration-300"
          />
          <div className="flex gap-3 overflow-x-auto py-1">
            {IMAGES.map((img, i) => (
              <img
                key={i}
                src={img}
                alt={`Vue ${i + 1}`}
                onClick={() => setMainImage(img)}
                className={`w-28 h-20 object-cover rounded-lg cursor-pointer flex-shrink-0 transition-all duration-300
                  ${mainImage === img ? 'ring-2 ring-primary ring-offset-2' : 'hover:opacity-80'}`}
              />
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-10 mb-10">
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold mb-3">{t('quartz.descTitle')}</h2>
              <p className="text-base-content/80 leading-relaxed">{t('quartz.desc')}</p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold mb-3">{t('quartz.equipTitle')}</h2>
              <ul className="space-y-2">
                {Array.isArray(equipements) && equipements.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-base-content/80">
                    <span className="text-primary mt-1 flex-shrink-0">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('quartz.infoTitle')}</h2>
            <div className="bg-base-200 p-6 rounded-xl space-y-5">
              <div>
                <h3 className="font-semibold text-sm text-primary uppercase tracking-wide">{t('quartz.capacity')}</h3>
                <p>{t('quartz.capacityVal')}</p>
              </div>
              <div>
                <h3 className="font-semibold text-sm text-primary uppercase tracking-wide">{t('quartz.surface')}</h3>
                <p>{t('quartz.surfaceVal')}</p>
              </div>
              <div>
                <h3 className="font-semibold text-sm text-primary uppercase tracking-wide">{t('quartz.rate')}</h3>
                <p className="text-2xl font-bold">{t('quartz.rateVal')} <span className="text-base font-normal text-base-content/60">{t('quartz.perNight')}</span></p>
              </div>
              <a
                href={AIRBNB_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary w-full gap-2"
              >
                {t('quartz.bookAirbnb')}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default AppartementQuartz;
